import { EventEmitter, Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { forkJoin, Observable } from "rxjs";

import {
  ViewService,
  SearchParams,
  CheckboxRenderer,
  CheckboxEditor,
  DeleteRowRenderer,
  AddRowHeader,
  ListViewDelegate,
  FacilityService,
  DatepickerEditor,
  WasteStream,
  WasteStreamService,
  ConfigService,
  TsdfCodeService,
  ManagementCodeService,
  ProcessCategoryService,
  FormCodeService,
  SourceCodeService,
  RegulatoryStatusService,
} from "../common";

@Component({
  selector: "waste-stream-view",
  templateUrl: "./auxiliarylist-view.html",
  standalone: false,
})
export class WasteStreamView implements ListViewDelegate<WasteStream> {
  errorMsg: string;
  public refresh: EventEmitter<void> = new EventEmitter<void>();
  public entity: WasteStream;
  public sourceColumns;
  public sourceComponents;
  gridKeyColumn: string = "tsdfProfile";
  physicalStates: string[];
  facilityCompanies: string[];
  mgmtCodes: string[];
  tsdfCodes: string[];
  formCodes: string[];
  sourceCodes: string[];
  procCategories: string[];
  regulatoryStatuses: string[];

  constructor(
    public route: ActivatedRoute,
    public viewService: ViewService,
    public dataService: WasteStreamService,
    private configService: ConfigService,
    private facilityService: FacilityService,
    private tsdfService: TsdfCodeService,
    private mgmtService: ManagementCodeService,
    private sourceService: SourceCodeService,
    private formService: FormCodeService,
    private procCatService: ProcessCategoryService,
    private regulatoryService: RegulatoryStatusService
  ) {
    viewService.setViewData({
      pageTitle: "Waste Streams",
      link: {},
      breadcrumbs: false,
    });
    this.sourceComponents = {
      chkBoxRenderer: CheckboxRenderer,
      chkBoxEditor: CheckboxEditor,
      deleteRow: DeleteRowRenderer,
      addRow: AddRowHeader,
      datePicker: DatepickerEditor,
    };

    this.getWSLists().subscribe((result) => {
      this.physicalStates = result[0].physicalStateOptions;
      this.tsdfCodes = result[1].data.map((u) => u.code);
      this.mgmtCodes = result[2].data.map((u) => u.code);
      this.formCodes = result[3].data.map((u) => u.code);
      this.sourceCodes = result[4].data.map((u) => u.code);
      this.procCategories = result[5].data.map((u) => u.name);
      this.regulatoryStatuses = result[6].data.map((u) => u.status);
      this.facilityCompanies = result[7].data.map((u) => u.company);

      this.sourceColumns = [
        {
          headerComponent: "addRow",
          headerComponentParams: { selectColumn: this.gridKeyColumn },
          cellRenderer: "deleteRow",
          minWidth: 70,
          maxWidth: 80,
          pinned: "left",
          sortable: false,
          filter: false,
          editable: false,
        },
        {
          headerName: "TSDF Profile",
          field: this.gridKeyColumn,
          minWidth: 80,
          maxWidth: 150,
          pinned: "left",
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Name",
          field: "name",
          minWidth: 200,
          maxWidth: 300,
          pinned: "left",
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Container Detail",
          field: "containerDetail",
          minWidth: 300,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "DOT Description",
          field: "dotDescription",
          minWidth: 300,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Corr",
          field: "corrosive",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Igni",
          field: "ignitable",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Reac",
          field: "reactive",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Toxi",
          field: "toxic",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "HM",
          field: "hm",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "RCRA",
          field: "rcra",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Phy. St.",
          field: "physicalState",
          minWidth: 100,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.physicalStates,
          },
        },
        {
          headerName: "TSDF Code",
          field: "tsdfCode",
          minWidth: 100,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.tsdfCodes,
          },
        },
        {
          headerName: "Mgmt Code",
          field: "mgmtCode",
          minWidth: 100,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.mgmtCodes,
          },
        },
        {
          headerName: "Form Code",
          field: "formCode",
          minWidth: 100,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.formCodes,
          },
        },
        {
          headerName: "Src. Code",
          field: "sourceCode",
          minWidth: 100,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.sourceCodes,
          },
        },
        {
          headerName: "Facility",
          field: "facility",
          minWidth: 200,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.facilityCompanies,
          },
        },
        {
          headerName: "Proc. Cat.",
          field: "processCategory",
          minWidth: 200,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.procCategories,
          },
        },
        {
          headerName: "Reg. St.",
          field: "regulatoryStatus",
          minWidth: 200,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: this.regulatoryStatuses,
          },
        },
        {
          headerName: "ERG",
          field: "ergNumber",
          minWidth: 75,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },

        {
          headerName: "Expires On",
          field: "profileExpiresOn",
          minWidth: 100,
          filter: "agDateColumnFilter",
          cellEditor: "datePicker",
          cellRenderer: (data) => {
            return data.value ? new Date(data.value).toLocaleDateString() : "";
          },
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Const#01",
          field: "constituent1",
          minWidth: 200,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "%#01",
          field: "percent1",
          minWidth: 50,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Const#02",
          field: "constituent2",
          minWidth: 200,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "%#02",
          field: "percent2",
          minWidth: 50,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Const#03",
          field: "constituent3",
          minWidth: 200,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "%#03",
          field: "percent3",
          minWidth: 50,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Const#04",
          field: "constituent4",
          minWidth: 200,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "%#04",
          field: "percent4",
          minWidth: 50,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "Const#05",
          field: "constituent5",
          minWidth: 200,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "%#05",
          field: "percent5",
          minWidth: 50,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "St. WC",
          field: "stateWasteCode",
          minWidth: 75,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC1",
          field: "wasteCode01",
          minWidth: 75,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC2",
          field: "wasteCode02",
          minWidth: 75,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC3",
          field: "wasteCode03",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC4",
          field: "wasteCode04",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC5",
          field: "wasteCode05",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC6",
          field: "wasteCode06",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC7",
          field: "wasteCode07",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC8",
          field: "wasteCode08",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC9",
          field: "wasteCode09",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "WC10",
          field: "wasteCode10",
          minWidth: 100,
          filter: "agTextColumnFilter",
          sortingOrder: ["asc", "desc"],
        },
        {
          headerName: "ERM Recycle Off Site",
          field: "ermRecycleOffsite",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "ERM Recycle On Site",
          field: "ermRecycleOnsite",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "SB 14 exempt",
          field: "sb14exempt",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Tri Exempt",
          field: "triexempt",
          minWidth: 75,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
        {
          headerName: "Act.",
          field: "active",
          minWidth: 50,
          filter: false,
          sortingOrder: ["asc", "desc"],
          cellRenderer: "chkBoxRenderer",
          cellEditor: "chkBoxEditor",
        },
      ];
    });
  }

  public getWSLists(): Observable<any[]> {
    return forkJoin([
      this.configService.get(),
      this.tsdfService.getActive(),
      this.mgmtService.getActive(),
      this.formService.getActive(),
      this.sourceService.getActive(),
      this.procCatService.getActive(),
      this.regulatoryService.getActive(),
      this.facilityService.getActive(),
    ]);
  }

  performSearch(searchParams: SearchParams): Observable<WasteStream[]> {
    return this.dataService.all(searchParams);
  }

  save(entity: WasteStream) {
    this.dataService.save(entity).subscribe(
      (en) => {
        this.errorMsg = null;
        this.refreshView();
      },
      (error) => {
        this.errorMsg = error;
        this.refreshView();
      }
    );
  }

  remove(entity: WasteStream) {
    if (window.confirm(`Are you sure you want to remove '${entity[this.gridKeyColumn]}'?`)) {
      this.dataService.remove(entity.id).subscribe((x) => {
        this.errorMsg = null;
        this.refreshView();
      });
    }
  }

  refreshView() {
    this.refresh.emit();
  }
}
